import React, { useState, useEffect } from "react";

// ─── localStorage read/unread tracking (mirrors SMRT Docs pattern) ──────────

const STORAGE_KEY = "smrtcop_notifications";

function getReadIds(): string[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? JSON.parse(raw) : [];
}

function saveReadIds(ids: string[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

function markIdsAsRead(ids: string[]): void {
  const existing = new Set(getReadIds());
  for (const id of ids) existing.add(id);
  saveReadIds([...existing]);
}

function isRead(id: string): boolean {
  return getReadIds().includes(id);
}

// ─── Traffic light helpers ──────────────────────────────────────────────────

type TrafficItem = {
  siteId: string;
  siteName: string;
  message: string;
  id?: string; // for green mark-as-read
};

export function getRedItems(
  sites: any[],
): TrafficItem[] {
  const items: TrafficItem[] = [];
  for (const site of sites) {
    if (site.assignedUserIds?.length === 0 || !site.assignedUserIds) {
      items.push({ siteId: site.id, siteName: site.name || site.meta?.siteId || site.id, message: "Assign Crew" });
    }
    if (site.status === "Complete") {
      items.push({ siteId: site.id, siteName: site.name || site.meta?.siteId || site.id, message: "Submit for Final Approval" });
    }
  }
  return items;
}

export function getYellowItems(
  sites: any[],
): TrafficItem[] {
  const items: TrafficItem[] = [];
  for (const site of sites) {
    if (site.status === "Active") {
      items.push({ siteId: site.id, siteName: site.name || site.meta?.siteId || site.id, message: "Waiting for Crew Submission" });
    }
    if (site.status === "Submitted") {
      items.push({ siteId: site.id, siteName: site.name || site.meta?.siteId || site.id, message: "Waiting for Client Approval" });
    }
  }
  return items;
}

export function getGreenItems(
  sites: any[],
): TrafficItem[] {
  const items: TrafficItem[] = [];
  for (const site of sites) {
    if (site.status === "Approved") {
      const greenId = `green:${site.id}`;
      if (!isRead(greenId)) {
        items.push({ siteId: site.id, siteName: site.name || site.meta?.siteId || site.id, message: "Approved by Client", id: greenId });
      }
    }
  }
  return items;
}

// ─── Modal Component ────────────────────────────────────────────────────────

type Props = {
  isOpen: boolean;
  onClose: () => void;
  filter: "red" | "yellow" | "green" | null;
  sites: any[];
  onViewSite: (siteId: string) => void;
};

const TITLES: Record<string, string> = {
  red: "Needs Attention",
  yellow: "Awaiting Action",
  green: "Approved",
};

const STATUS_COLORS: Record<string, string> = {
  red: "#c62828",
  yellow: "#d4a017",
  green: "#1faa59",
};

type GroupedSite = { siteId: string; siteName: string; items: { message: string; id?: string }[] };

function groupBySite(items: TrafficItem[]): GroupedSite[] {
  const map: Record<string, GroupedSite> = {};
  for (const item of items) {
    if (!map[item.siteId]) {
      map[item.siteId] = { siteId: item.siteId, siteName: item.siteName, items: [] };
    }
    map[item.siteId].items.push({ message: item.message, id: item.id });
  }
  return Object.values(map);
}

export default function SMRTCOPTrafficModal({ isOpen, onClose, filter, sites, onViewSite }: Props) {
  const [expandedSites, setExpandedSites] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [, setRefresh] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setRefresh((n) => n + 1);
      setSelectedIds([]);
    }
  }, [isOpen, filter]);

  if (!isOpen) return null;

  const title = filter ? TITLES[filter] : "All Notifications";
  const rawItems =
    filter === "red" ? getRedItems(sites) :
    filter === "yellow" ? getYellowItems(sites) :
    filter === "green" ? getGreenItems(sites) :
    [];

  const groups = groupBySite(rawItems);
  const statusColor = STATUS_COLORS[filter || "red"] || "#9aa0a6";
  const isGreen = filter === "green";

  const allGreenIds: string[] = isGreen
    ? groups.flatMap((g) => g.items.filter((i) => i.id).map((i) => i.id!))
    : [];
  const allSelected = isGreen && allGreenIds.length > 0 && allGreenIds.every((id) => selectedIds.includes(id));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : [...allGreenIds]);
  };

  const handleMarkAsRead = () => {
    markIdsAsRead(selectedIds);
    setSelectedIds([]);
    setRefresh((n) => n + 1);
  };

  const handleViewAndMark = (group: GroupedSite) => {
    if (isGreen) {
      markIdsAsRead(group.items.filter((i) => i.id).map((i) => i.id!));
    }
    onClose();
    onViewSite(group.siteId);
  };

  const toggleExpand = (siteId: string) => {
    setExpandedSites((prev) =>
      prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId],
    );
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "550px", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>{title}</h2>
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>

        {isGreen && groups.length > 0 && (
          <div style={{ padding: "8px 20px", borderBottom: "1px solid #555", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#9aa0a6", fontSize: "0.8rem", cursor: "pointer" }}>
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ accentColor: "#1faa59" }} />
              Select All
            </label>
            {selectedIds.length > 0 && (
              <button
                className="btn-icon"
                style={{ background: "#1faa59", border: "none", borderRadius: 6, color: "#fff", padding: "6px 20px", fontSize: "0.82rem", cursor: "pointer", whiteSpace: "nowrap", width: "auto", minWidth: 130 }}
                onClick={handleMarkAsRead}
              >
                Mark As Read
              </button>
            )}
          </div>
        )}

        <div className="smrtdocs-modal-body" style={{ overflowY: "auto", flex: 1 }}>
          {groups.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {groups.map((group) => (
                <div key={group.siteId}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      background: "#1c1c1c",
                      border: "1px solid #2a2a2a",
                      borderRadius: 6,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "#fff", fontSize: "0.95rem" }}>{group.siteName}</div>
                      <div style={{ fontSize: "0.8rem", color: statusColor }}>
                        {group.items.length} item{group.items.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        className="btn-icon"
                        style={{ width: 32, height: 32, fontSize: 12, background: "#4b5563", border: "1px solid #6b7280", borderRadius: 6, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                        title={expandedSites.includes(group.siteId) ? "Collapse" : "Expand"}
                        onClick={() => toggleExpand(group.siteId)}
                      >
                        {expandedSites.includes(group.siteId) ? "▶" : "▼"}
                      </button>
                      <button
                        className="btn-icon smrtdocs-btn-view"
                        style={{ width: 32, height: 32, fontSize: 14, flexShrink: 0 }}
                        title="View"
                        onClick={() => handleViewAndMark(group)}
                      >
                        👁
                      </button>
                    </div>
                  </div>

                  {expandedSites.includes(group.siteId) && (
                    <div style={{ paddingLeft: 12, paddingTop: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                      {group.items.map((item, j) => (
                        <div key={j} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.88rem", color: "#9aa0a6" }}>
                          {isGreen && item.id && (
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(item.id)}
                              onChange={() => toggleSelect(item.id!)}
                              style={{ accentColor: "#1faa59", cursor: "pointer" }}
                            />
                          )}
                          {item.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#9aa0a6", textAlign: "center", padding: "20px 0" }}>
              No items
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
